/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Container from "../../components/Container";
import Button from "../../components/ui/Button";
import ButtonGroup from "../../components/ui/ButtonGroup";
import { ROUTES } from "../../constants/constants";
import { useForm } from "react-hook-form";
import Form from "../../components/ui/form/Form";
import FormFieldsWrapper from "../../components/ui/form/FormFieldsWrapper";
import NavigationHeader from "../../components/NavigationHeader";
import { useToast } from "../../components/ui/Toast";
import ClientFieldsGroup from "../../components/client/ClientFieldsGroup";
import { useClientFields } from "../../lib/react-query/useClientFields";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useAuth } from "../../hooks/useAuth";

const StyledClientProfileEditForm = styled.div`
  display: grid;
  gap: 24px;
  place-items: start;
  padding: 24px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
`;

const ClientProfileEdit = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { edit, isEditPending } = useAuth();
  const { data: clientFields, isLoading: clientFieldsLoading } = useClientFields();
  const form = useForm();
  const { handleSubmit, setError } = form;

  const handleCancel = (e) => {
    e.preventDefault();
    navigate(ROUTES.CLIENT_PROFILE);
  };

  const onSubmit = async (data) => {
    try {
      await edit(data);
      navigate(ROUTES.CLIENT_PROFILE);
      toast({
        title: "success",
        description: "Your profile has been successfully updated.",
        variant: "success",
      });
    } catch (error) {
      setError("root", { message: error.message });
    }
  };

  if (clientFieldsLoading) {
    return (
      <Container>
        <NavigationHeader title="my_profile" back={ROUTES.CLIENT_PROFILE} />
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader title="my_profile" back={ROUTES.CLIENT_PROFILE} />
      <StyledClientProfileEditForm>
        <Form>
          <FormFieldsWrapper>
            <ClientFieldsGroup form={form} fields={clientFields} hasClientData={true} />
          </FormFieldsWrapper>
          <ButtonGroup gap="large">
            <Button variant="outline" title="Cancel changes" onClick={handleCancel} />
            <Button
              variant="primary"
              title="Save changes"
              onClick={handleSubmit(onSubmit)}
              loading={isEditPending}
              fullWidth
            />
          </ButtonGroup>
        </Form>
      </StyledClientProfileEditForm>
    </Container>
  );
};

export default ClientProfileEdit;
